import { useState, useEffect, useCallback, useRef } from 'react';
import { LogOut } from 'lucide-react';
import { supabase, isSupabaseConnected } from './supabaseClient'; // Import the supabase client
import { useTenant, useTenantType } from './context/TenantContext';
import WarehouseTerminal from './WarehouseTerminal';
import ClientDashboard from './ClientDashboard';
import LiveManifest from './LiveManifest';
import LoginPortal from './LoginPortal';
import ClientManager from './ClientManager'; // Import ClientManager
import TrackingPage from './TrackingPage';
import EncomiendaWizard from './EncomiendaWizard';
import PendingEncomiendas from './PendingEncomiendas';
import ProductionTab from './ProductionTab';
import GuiasPortal from './GuiasPortal';
import FacturacionPanel from './components/Facturacion/FacturacionPanel';
import FinanzasPage from './components/Finanzas/FinanzasPage';
import AdminDashboard from './components/AdminDashboard';
import RecepcionDigital from './components/RecepcionDigital/RecepcionDigital';
import ViajeroEncomienda from './components/ViajeroEncomienda/ViajeroEncomienda';
import { getNextPackageNumber } from './utils/packageCounter';

const SIVARSEND_LOGO = "https://nsqrywqnuwbhzbevvphk.supabase.co/storage/v1/object/public/tenant-logos/SivarSendLogoClean.png";

// ── Permission matrix ─────────────────────────────────────────────────────────
// Single source of truth for feature gates. Never check user.role directly
// in components — always use the userPermissions object derived from this.
const PERMISSIONS = {
    admin: {
        canCloseManifest:      true,
        canExportCosteo:       true,
        canViewCosteo:         true,
        canUseAI:              true,
        canAddPackages:        true,
        canEditPackages:       true,
        canDeletePackages:     true,
        canManageClients:      true,
        canViewAllManifests:   true,
        canRequestClose:       false,
        canViewDeclaration:    true,
        canEditCustomsConfig:  true,
        canViewGuias:          true,
        canViewClientes:       true,
        canEditClientes:       true,
        canViewEntrada:        true,
        canViewManifest:       true,
        canViewFacturacion:    true,
        canViewFinanzas:       true,
        canViewRecepcionDigital: true,
        canViewAdminDashboard: true,
    },
    manager: {
        canCloseManifest:      true,
        canExportCosteo:       true,
        canViewCosteo:         true,
        canUseAI:              true,
        canAddPackages:        true,
        canEditPackages:       true,
        canDeletePackages:     true,
        canManageClients:      true,
        canViewAllManifests:   true,
        canRequestClose:       false,
        canViewDeclaration:    true,
        canEditCustomsConfig:  true,
        canViewGuias:          true,
        canViewClientes:       true,
        canEditClientes:       true,
        canViewEntrada:        true,
        canViewManifest:       true,
        canViewFacturacion:    true,
        canViewFinanzas:       true,
        canViewRecepcionDigital: false,
        canViewAdminDashboard: true,
    },
    dispatch: {
        canCloseManifest:      false,
        canExportCosteo:       false,
        canViewCosteo:         false,
        canUseAI:              false,
        canAddPackages:        false,
        canEditPackages:       false,
        canDeletePackages:     false,
        canManageClients:      false,
        canViewAllManifests:   false,
        canRequestClose:       false,
        canViewDeclaration:    false,
        canEditCustomsConfig:  false,
        canViewGuias:          true,
        canViewClientes:       true,
        canEditClientes:       false,
        canViewEntrada:        false,
        canViewManifest:       false,
        canViewFacturacion:    false,
        canViewFinanzas:       false,
        canViewRecepcionDigital: false,
        canViewAdminDashboard: false,
    },
    bodega: {
        canCloseManifest:      false,
        canExportCosteo:       false,
        canViewCosteo:         false,
        canUseAI:              true,
        canAddPackages:        true,
        canEditPackages:       true,
        canDeletePackages:     true,
        canManageClients:      false,
        canViewAllManifests:   true,
        canRequestClose:       true,
        canViewDeclaration:    false,
        canEditCustomsConfig:  false,
        canViewGuias:          true,
        canViewClientes:       false,
        canEditClientes:       false,
        canViewEntrada:        true,
        canViewManifest:       true,
        canViewFacturacion:    true,
        canViewFinanzas:       true,
        canViewRecepcionDigital: false,
        canViewAdminDashboard: false,
    },
    // Rol nuevo para Recepción Digital — todavía no existe en staff_members;
    // requiere migración (comando-ops-agent) antes de poder iniciar sesión con él.
    sivarsend_operator: {
        canCloseManifest:      false,
        canExportCosteo:       false,
        canViewCosteo:         false,
        canUseAI:              false,
        canAddPackages:        false,
        canEditPackages:       false,
        canDeletePackages:     false,
        canManageClients:      false,
        canViewAllManifests:   false,
        canRequestClose:       false,
        canViewDeclaration:    false,
        canEditCustomsConfig:  false,
        canViewGuias:          false,
        canViewClientes:       false,
        canEditClientes:       false,
        canViewEntrada:        false,
        canViewManifest:       false,
        canViewFacturacion:    false,
        canViewFinanzas:       false,
        canViewRecepcionDigital: true,
        canViewAdminDashboard: false,
    },
    client: {
        canCloseManifest:      false,
        canExportCosteo:       false,
        canViewCosteo:         false,
        canUseAI:              false,
        canAddPackages:        false,
        canEditPackages:       false,
        canDeletePackages:     false,
        canManageClients:      false,
        canViewAllManifests:   false,
        canRequestClose:       false,
        canViewDeclaration:    false,
        canEditCustomsConfig:  false,
        canViewClientes:       false,
        canEditClientes:       false,
        canViewEntrada:        false,
        canViewManifest:       false,
        canViewFacturacion:    false,
        canViewFinanzas:       false,
        canViewRecepcionDigital: false,
        canViewAdminDashboard: false,
    },
};

// ── Package ID helpers ─────────────────────────────────────────────────────────
// Builds a globally-unique storage key from manifest ID + display ID.
// Example: buildGlobalPackageId('MAN-2026-011', 'IO-1') → 'M011-IO-1'
const buildGlobalPackageId = (manifestId, displayId) => {
    const parts = (manifestId || '').split('-');
    const manifestNum = parts[2] || '000';
    return 'M' + manifestNum + '-' + displayId;
};

const translations = {
        es: {
            opsCommand: "Portal", clientPortal: "Cliente Aviance", newEntry: "Nueva Entrada", manifest: "Manifiesto",
            reset: "RESETEAR", exit: "Salir", casillero: "Casillero", encomienda: "Encomienda", createBox: "Consolidar en Caja",
            clientManager: "Gestor de Clientes",
            emptyManifest: "No hay paquetes en este manifiesto. Comience ingresando un nuevo paquete.",
        },    en: {
        opsCommand: "Portal", clientPortal: "Aviance Client", newEntry: "New Entry", manifest: "Manifest",
        reset: "RESET", exit: "Exit", casillero: "Locker", encomienda: "Shipment", createBox: "Consolidate in Box",
        clientManager: "Client Manager",
        emptyManifest: "No packages in this manifest yet. Start by entering a new package.",
    }
};

const App = () => {
    const [user, setUser] = useState(null);
    const [currentView, setCurrentView] = useState('login');

    // ── Mobile logo hamburger menu (admin/manager only) ──────────────────────
    const [logoMenuOpen, setLogoMenuOpen] = useState(false);
    const logoMenuRef = useRef(null);
    const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
    // window width isn't reactive — only read inside event handlers (logo onClick below), never in render logic

    /* eslint-disable */
    useEffect(() => {
        if (!logoMenuOpen) return;
        const handleClickOutside = (e) => {
            if (logoMenuRef.current && !logoMenuRef.current.contains(e.target)) {
                setLogoMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [logoMenuOpen]);
    /* eslint-enable */

    /* eslint-disable */
    useEffect(() => {
        setLogoMenuOpen(false);
    }, [currentView]);
    /* eslint-enable */

    /* eslint-disable */
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') setLogoMenuOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);
    /* eslint-enable */

    /* eslint-disable */
    useEffect(() => {
        document.body.style.overflow = logoMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [logoMenuOpen]);
    /* eslint-enable */
    // Client portal session — completely separate from admin/warehouse user
    const [clientUser, setClientUser] = useState(() => {
        try {
            const stored = sessionStorage.getItem('verified_customer');
            return stored ? JSON.parse(stored) : null;
        } catch { return null; }
    });
    const [language] = useState('es');
    const t = translations[language];

    const [customers, setCustomers] = useState([]);
    const [shipments, setShipments] = useState([]);
    const [boxes, setBoxes] = useState([]);
    const [currentManifest, setCurrentManifest] = useState(null);
    const [activeSessionManifest, setActiveSessionManifest] = useState(null);
    const workingManifest = activeSessionManifest || currentManifest;
    const [loading, setLoading] = useState(true);
    const [supaBaseError, setSupaBaseError] = useState(false);
    const [supaBaseErrorMessage, setSupaBaseErrorMessage] = useState(null);

    // Nueva Entrada sub-mode: 'casillero' | 'encomienda'
    const [entryMode, setEntryMode] = useState('casillero');
    // When PendingEncomiendas "Continuar →" is clicked, store the draft ID here
    const [resumeEncomiendaId, setResumeEncomiendaId] = useState(null);

    // Step 2 / Step 3 diagnostics — logs every time the user state changes
    useEffect(() => {
        console.log('[App] user state changed — role:', user?.role);
        console.log('[App] Full user object:', user);
        console.log('[App] "Gestor de Clientes" condition (user && user.role === "admin"):', !!(user && user.role === 'admin'));
    }, [user]);

    const { tenant, tenantReady } = useTenant();
    // Gate viajero: solo true con tenant_type === 'viajero' ya resuelto. null
    // (cargando) o columna ausente caen en la UI de agencia de siempre.
    const isViajero = useTenantType() === 'viajero';

    useEffect(() => {
        document.title = tenant?.company_name
            ? `${tenant.company_name} · Portal`
            : 'SivarSend';
    }, [tenant?.company_name]);

    // Extracted so both the mount effect and onAuthStateChange can call it.
    // Session guard prevents RLS from blocking the query before auth is restored.
    /* eslint-disable */
    const fetchInitialData = useCallback(async () => {
        if (!tenant) return;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            console.log('[fetchInitialData] No active session — aborting fetch');
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            // Phase 1: customers + manifest + boxes in parallel
            // (none depend on each other)
            const customersQuery = supabase.from('customers').select('*');
            const manifestQuery = supabase.from('manifests').select('*').eq('status', 'OPEN');
            const boxesQuery = supabase.from('boxes').select('*');
            const [customersRes, manifestRes, boxesRes] =
                await Promise.all([
                    customersQuery,
                    manifestQuery.maybeSingle(),
                    boxesQuery,
                ]);

            setCustomers(customersRes.data || []);
            setBoxes(boxesRes.data || []);

            const manifestData = manifestRes.data || null;
            setCurrentManifest(manifestData);

            // Phase 2: shipments — depends on manifest ID
            // Must run after Phase 1 resolves
            if (!manifestData) {
                console.log('[fetchInitialData] No open manifest — clearing shipments');
                setShipments([]);
            } else {
                const { data: shipmentsData, error: shipmentsError } =
                    await supabase
                        .from('shipments')
                        .select(`
                            *,
                            customers (
                                id,
                                full_name,
                                phone_number
                            )
                        `)
                        .eq('manifest_id', manifestData.id)
                        .order('entry_timestamp', { ascending: false });
                if (shipmentsError) throw shipmentsError;
                setShipments(shipmentsData || []);
            }

            console.log(
                '[fetchInitialData] parallel fetch complete —',
                'customers:', (customersRes.data || []).length,
                'shipments:', manifestData ? 'fetched' : 'none (no manifest)',
                'boxes:', (boxesRes.data || []).length
            );
        } catch (error) {
            console.error('[fetchInitialData] Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [tenant, user]);
    /* eslint-enable */

    /* eslint-disable */
    useEffect(() => {
        if (!tenant) return;
        if (user && user.role) {
            fetchInitialData();
        }
    }, [tenant, user, fetchInitialData]);
    /* eslint-enable */

    useEffect(() => {
        if (!isSupabaseConnected) {
            setSupaBaseError(true);
            setSupaBaseErrorMessage('Handshake Failed: Check @.env Keys');
            return;
        }

        const customerSubscription = supabase.channel('realtime-customers')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'customers' }, (payload) => {
                if (payload.eventType === 'INSERT') {
                    setCustomers(current => [...current, payload.new]);
                }
                if (payload.eventType === 'UPDATE') {
                    setCustomers(current => current.map(c => c.id === payload.new.id ? payload.new : c));
                }
                if (payload.eventType === 'DELETE') {
                    setCustomers(current => current.filter(c => c.id !== payload.old.id));
                }
            })
            .subscribe();

        const shipmentSubscription = supabase.channel('realtime-shipments')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'shipments' }, (payload) => {
                if (payload.eventType === 'INSERT') {
                    setShipments(current => [...current, payload.new]);
                }
                if (payload.eventType === 'UPDATE') {
                    setShipments(current => current.map(s => s.id === payload.new.id ? payload.new : s));
                }
                if (payload.eventType === 'DELETE') {
                    setShipments(current => current.filter(s => s.id !== payload.old.id));
                }
            })
            .subscribe();

        const boxSubscription = supabase.channel('realtime-boxes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'boxes' }, (payload) => {
                if (payload.eventType === 'INSERT') {
                    setBoxes(current => [...current, payload.new]);
                }
                if (payload.eventType === 'UPDATE') {
                    setBoxes(current => current.map(b => b.box_id === payload.new.box_id ? payload.new : b));
                }
                if (payload.eventType === 'DELETE') {
                    setBoxes(current => current.filter(b => b.box_id !== payload.old.box_id));
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(customerSubscription);
            supabase.removeChannel(shipmentSubscription);
            supabase.removeChannel(boxSubscription);
        };
    }, [fetchInitialData]);

    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('[App] auth event:', event);

            if (event === 'SIGNED_OUT' || !session) {
                setUser(null);
                setCurrentView('login');
                setLoading(false);
                return;
            }

            // Only runs on page refresh — fresh login is handled entirely in LoginPortal
            // which queries staff_members and passes the enriched user via onLogin.
            if (event === 'INITIAL_SESSION') {
                if (session?.user) {
                    const { data: staffData } = await supabase
                        .from('staff_members')
                        .select('role, username, tenant_id, is_super_admin')
                        .eq('id', session.user.id)
                        .maybeSingle();

                    // Guard: if tenant_id is missing from JWT and user is not super_admin, sign out and redirect
                    const jwtTenantId = session.user.app_metadata?.tenant_id;
                    if (!jwtTenantId && !staffData?.is_super_admin) {
                        console.warn('[App] INITIAL_SESSION — tenant_id missing from JWT for user:', staffData?.username, '— signing out');
                        await supabase.auth.signOut();
                        setUser(null);
                        setCurrentView('login');
                        setLoading(false);
                        alert('Tu sesión no tiene contexto de tenant. Contacta al administrador.');
                        return;
                    }

                    const role = staffData?.role || 'admin';
                    const username = staffData?.username || session.user.email;
                    setUser({
                        ...session.user,
                        role,
                        username,
                        full_name: username,
                        is_super_admin: staffData?.is_super_admin || false,
                    });
                    if (role === 'admin') setCurrentView('client_manager');
                    else if (role === 'dispatch') setCurrentView('guias');
                    else setCurrentView('customer_service');
                }
                setLoading(false);
                return;
            }
        });
        return () => subscription.unsubscribe();
    }, []);

    const handleAddShipment = async (data) => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            alert('Sesión expirada. Por favor recarga la página e inicia sesión de nuevo.');
            return;
        }
        console.log('[handleAddShipment] session uid:', session?.user?.id);
        let processedCustomerId = data.customerId.toUpperCase().trim();

        if (data.serviceType === 'casillero') {
            // FIX 1: Validate customer exists in DB before inserting shipment
            const { data: customerCheck } = await supabase
                .from('customers')
                .select('id')
                .eq('id', processedCustomerId)
                .maybeSingle();

            if (!customerCheck) {
                alert('Customer ID not found. Verify the ID and try again.');
                return;
            }
        } else {
            // Encomienda: look up customer by phone first — only create if not found
            const cleanPhone = (data.customerPhone || '').replace(/\D/g, '');
            const { data: existingCustomer } = await supabase
                .from('customers')
                .select('id, full_name')
                .ilike('phone_number', cleanPhone)
                .maybeSingle();

            if (existingCustomer) {
                // Reuse existing customer — avoid duplicate insert
                processedCustomerId = existingCustomer.id;
                console.log('[handleAddShipment] Found existing customer:', existingCustomer.id);
            } else {
                // New customer — insert with initials-based ID from WarehouseTerminal
                const customerPayload = {
                    id: processedCustomerId,
                    full_name: (data.customerName || '').trim(),
                    phone_number: cleanPhone
                };
                console.log('[handleAddShipment] INSERT new customer payload:', customerPayload);
                const { error: customerError } = await supabase.from('customers').insert([customerPayload]);
                if (customerError) {
                    console.error('[handleAddShipment] Customer insert failed:', customerError);
                    alert('Failed to create customer: ' + customerError.message);
                    return;
                }
            }
        }

        // Use whichever manifest is active (session or current open)
        const openManifest = workingManifest;
        if (!openManifest) {
            alert('No hay manifiesto activo. Contacte al admin.');
            return;
        }
        console.log('[WorkingManifest] active:', workingManifest?.id, 'isSession:', !!activeSessionManifest);

        // Count ALL confirmed packages in manifest (casillero + complete encomiendas)
        const packageNumber = await getNextPackageNumber(supabase, openManifest.id);
        const displayId = `${processedCustomerId}-${packageNumber}`;
        const globalId = buildGlobalPackageId(openManifest.id, displayId);
        console.log('[IDs] display:', displayId, 'global:', globalId, 'manifest:', openManifest.id);

        // Collision guard — re-count if global_package_id already taken (e.g. concurrent saves)
        let uniquePackageId = displayId;
        let globalPackageId = globalId;

        const { data: existing } = await supabase
            .from('shipments')
            .select('id')
            .eq('global_package_id', globalId)
            .maybeSingle();

        if (existing) {
            const newNumber = await getNextPackageNumber(supabase, openManifest.id);
            uniquePackageId = `${processedCustomerId}-${newNumber}`;
            globalPackageId = buildGlobalPackageId(openManifest.id, uniquePackageId);
            console.warn('[Collision detected] Retrying with:', uniquePackageId, globalPackageId);
        }

        // Upload photo to Supabase Storage if provided
        let photoUrl = null;
        if (data.photoFile) {
            const ext = data.photoFile.name.split('.').pop();
            const filePath = `${processedCustomerId}/${uniquePackageId}-${Date.now()}.${ext}`;
            const { error: uploadError } = await supabase.storage
                .from('package-photos')
                .upload(filePath, data.photoFile, { upsert: false });
            if (uploadError) {
                console.error('[handleAddShipment] Photo upload failed:', uploadError);
                alert('Photo upload failed: ' + uploadError.message + '\nPackage will be saved without photo.');
            } else {
                const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(filePath);
                photoUrl = urlData.publicUrl;
            }
        }

        const shipmentPayload = {
            customer_id:       processedCustomerId,
            unique_package_id: uniquePackageId,
            global_package_id: globalPackageId,
            entry_timestamp:   new Date().toISOString(),
            tracking_number:   data.tracking                    || null,
            weight_lb:         data.serviceType === 'casillero' ? null : (parseFloat(data.weight) || null),
            dimensions:        data.serviceType === 'casillero' ? null : (data.dimensions || null),
            recipient_name:    data.customerName || data.recipientName || null,
            recipient_phone:   data.recipientPhone              || null,
            recipient_address: data.recipientAddress            || null,
            shipping_status:    data.shippingStatus              || 'Send Immediately',
            shipment_type:      data.serviceType === 'encomienda' ? 'ENCOMIENDA' : 'CASILLERO',
            photo_url:          photoUrl,
            manifest_id:        openManifest.id,
            entry_manifest_id:  openManifest.id,
        };
        console.log('[handleAddShipment] INSERT shipment payload:', shipmentPayload);
        console.log('[handleAddShipment] recipient_name:', shipmentPayload.recipient_name, 'customerName:', data.customerName, 'recipientName:', data.recipientName);

        // FIX 4: Log the full error object on shipment insert failure
        const { error: shipmentError } = await supabase.from('shipments').insert([shipmentPayload]);
        if (!shipmentError) {
            console.log('[VERIFY SAVE] Shipment saved — display:', uniquePackageId, 'global:', globalPackageId, 'manifest:', openManifest.id);
        }
        if (shipmentError) {
            console.error('[handleAddShipment] Shipment insert FAILED — full error object:', shipmentError);
            console.error('[handleAddShipment] error.code:', shipmentError.code);
            console.error('[handleAddShipment] error.message:', shipmentError.message);
            console.error('[handleAddShipment] error.details:', shipmentError.details);
            console.error('[handleAddShipment] error.hint:', shipmentError.hint);
            alert('Failed to save package: ' + shipmentError.message);
        }
    };

    const handleUpdateShipment = async (id, updatedData) => {
        await supabase.from('shipments').update(updatedData).eq('id', id);
        // Optimistic local update — UI reflects change immediately
        // without waiting for Realtime event
        setShipments(current =>
            current.map(s => s.id === id ? { ...s, ...updatedData } : s)
        );
    };

    const handleDeleteShipment = (shipmentId) => {
        setShipments(current => current.filter(s => s.id !== shipmentId));
    };

    const handleAddBox = (newBox) => {
        setBoxes(current => [...current, newBox]);
    };

    const handleUpdateBox = async (boxId, updatedData) => {
        await supabase.from('boxes').update(updatedData).eq('box_id', boxId);
        setBoxes(current => current.map(b => b.box_id === boxId ? { ...b, ...updatedData } : b));
    };

    // UI state only. Confirmation, the invoices billing guard and the actual
    // DELETE live in handleDeleteBoxGuarded / assertBoxDeletable in
    // LiveManifest.js — deleting again here would double-fire the request.
    const handleDeleteBox = (boxId) => {
        setBoxes(current => current.filter(b => b.box_id !== boxId));
    };
    
    // useCallback with [] gives handleLogin a stable reference across re-renders.
    // Without this, LoginPortal's checkExistingSession effect would re-fire on
    // every App re-render (because onLogin is in its dep array), potentially
    // overwriting the role with a stale fallback value.
    const handleLogin = useCallback((userData) => {
        console.log('[App] handleLogin — role:', userData?.role);

        // Client portal — completely separate from admin/warehouse app
        if (userData?.role === 'client') {
            sessionStorage.setItem('verified_customer', JSON.stringify(userData));
            setClientUser(userData);
            return;
        }

        setUser(userData);
        if (userData?.role === 'admin') {
            setCurrentView('client_manager');
        } else if (userData?.role === 'dispatch') {
            setCurrentView('guias');
        } else {
            setCurrentView('customer_service');
        }
        setLoading(false);
    }, []);

    const handleCloseManifest = async (pin) => {
        if (!currentManifest) return { success: false, error: 'No open manifest.' };

        // Validate PIN for warehouse staff
        if (user.role !== 'admin') {
            const { data: staff } = await supabase
                .from('staff_members')
                .select('closing_pin')
                .eq('username', user.username)
                .maybeSingle();
            if (!staff || staff.closing_pin !== pin) {
                return { success: false, error: 'PIN incorrecto. Intente de nuevo.' };
            }
        }

        const currentManifestId = currentManifest.id;

        // Count casillero SHIP shipments
        const { count: shipCount } = await supabase
            .from('shipments')
            .select('*', { count: 'exact', head: true })
            .eq('manifest_id', currentManifestId)
            .eq('shipping_status', 'Send Immediately');

        // Count encomienda packages in COMPLETE encomiendas
        const { data: manifestEncs } = await supabase
            .from('encomiendas')
            .select('id')
            .eq('manifest_id', currentManifestId)
            .eq('status', 'COMPLETE');

        let encPkgCount = 0;
        if (manifestEncs && manifestEncs.length > 0) {
            const encIds = manifestEncs.map(e => e.id);
            const { count: epCount } = await supabase
                .from('encomienda_packages')
                .select('*', { count: 'exact', head: true })
                .in('encomienda_id', encIds);
            encPkgCount = epCount || 0;
        }

        const totalPackages = (shipCount || 0) + encPkgCount;

        // Count HOLD shipments (casillero only)
        const { count: totalHold } = await supabase
            .from('shipments')
            .select('*', { count: 'exact', head: true })
            .eq('manifest_id', currentManifestId)
            .eq('shipping_status', 'Postpone to Next Delivery');

        console.log('[CloseManifest] Counts:', { shipCount, encPkgCount, totalPackages, totalHold });

        /*
         * -- Fix total_packages for already-closed manifests (run once in Supabase SQL editor):
         * UPDATE manifests m
         * SET total_packages = (
         *   SELECT COUNT(s.id) FROM shipments s
         *   WHERE s.manifest_id = m.id AND s.shipping_status = 'Send Immediately'
         * ) + (
         *   SELECT COUNT(ep.id)
         *   FROM encomienda_packages ep
         *   JOIN encomiendas e ON e.id = ep.encomienda_id
         *   WHERE e.manifest_id = m.id AND e.status = 'COMPLETE'
         * )
         * WHERE m.status = 'CLOSED';
         */

        // Close current manifest
        const { error: closeError } = await supabase
            .from('manifests')
            .update({
                status: 'CLOSED',
                closed_at: new Date().toISOString(),
                closed_by: user.username,
                total_packages: totalPackages,
                total_hold: totalHold || 0,
            })
            .eq('id', currentManifestId);

        if (closeError) {
            return { success: false, error: 'Error al cerrar manifiesto: ' + closeError.message };
        }

        await supabase
            .from('shipments')
            .update({ manifest_status: 'CLOSED' })
            .eq('manifest_id', currentManifestId);

        console.log('[closeManifest] shipments marked CLOSED for:', currentManifestId);

        // Generate new manifest ID via RPC
        const { data: newManifestId, error: rpcError } = await supabase
            .rpc('generate_manifest_id', { p_tenant_id: tenant?.id });

        if (rpcError || !newManifestId) {
            // Rollback — re-open current manifest
            await supabase
                .from('manifests')
                .update({ status: 'OPEN', closed_at: null, closed_by: null, total_packages: null, total_hold: null })
                .eq('id', currentManifestId);
            return { success: false, error: 'Error al generar nuevo manifiesto. Se revirtió el cierre.' };
        }

        // Insert new manifest
        const { error: insertError } = await supabase
            .from('manifests')
            .insert([{ id: newManifestId, status: 'OPEN', opened_at: new Date().toISOString() }]);

        if (insertError) {
            // Rollback — re-open current manifest
            await supabase
                .from('manifests')
                .update({ status: 'OPEN', closed_at: null, closed_by: null, total_packages: null, total_hold: null })
                .eq('id', currentManifestId);
            return { success: false, error: 'Error al crear nuevo manifiesto: ' + insertError.message };
        }

        // Move HOLD shipments to new manifest (never delete)
        await supabase
            .from('shipments')
            .update({ manifest_id: newManifestId })
            .eq('manifest_id', currentManifestId)
            .eq('shipping_status', 'Postpone to Next Delivery');

        // Update state directly — avoids fetchInitialData → getSession → TOKEN_REFRESHED loop
        setCurrentManifest({ id: newManifestId, status: 'OPEN', opened_at: new Date().toISOString() });
        setShipments(prev =>
            prev
                .filter(s => s.shipping_status === 'Postpone to Next Delivery')
                .map(s => ({ ...s, manifest_id: newManifestId }))
        );

        return { success: true, newManifestId };
    };

    const handleReopenLastManifest = async () => {
        if (user?.role !== 'admin') return;

        const { data: closedMans, error: fetchErr } = await supabase
            .from('manifests')
            .select('id, closed_at, total_packages')
            .eq('status', 'CLOSED')
            .order('closed_at', { ascending: false })
            .limit(1);

        if (fetchErr || !closedMans?.length) {
            return { success: false, error: 'No hay manifiestos cerrados para revertir.' };
        }

        const targetManifest = closedMans[0];

        const { count: liveCount } = await supabase
            .from('shipments')
            .select('*', { count: 'exact', head: true })
            .eq('manifest_id', currentManifest.id);

        if ((liveCount || 0) > 0) {
            return { success: false, error: 'El manifiesto activo tiene paquetes. Muévelos antes de revertir.' };
        }

        await supabase
            .from('manifests')
            .update({ status: 'CLOSED', closed_at: new Date().toISOString(), closed_by: user.username })
            .eq('id', currentManifest.id);

        const { error: reopenErr } = await supabase
            .from('manifests')
            .update({ status: 'OPEN', closed_at: null, closed_by: null })
            .eq('id', targetManifest.id);

        if (reopenErr) {
            await supabase
                .from('manifests')
                .update({ status: 'OPEN' })
                .eq('id', currentManifest.id);
            return { success: false, error: 'Error al reabrir manifiesto: ' + reopenErr.message };
        }

        await supabase
            .from('shipments')
            .update({ manifest_status: 'OPEN' })
            .eq('manifest_id', targetManifest.id);

        setCurrentManifest({ id: targetManifest.id, status: 'OPEN' });
        console.log('[ReopenLastManifest] Reverted to:', targetManifest.id, '| Closed empty:', currentManifest.id);
        return { success: true, reopenedId: targetManifest.id };
    };

    // Derived from PERMISSIONS — use this everywhere, never check user.role in components
    const userPermissions = PERMISSIONS[user?.role] || PERMISSIONS.client;

    // Tenant feature flags — derived server-side from tenant_integrations via trigger,
    // projected onto tenants.features (JSONB array). Anon-readable, so gates work
    // on the login page too. Add new features by inserting a row into tenant_integrations;
    // the trigger updates tenant.features automatically.
    const hasFeature = (name) => Array.isArray(tenant?.features) && tenant.features.includes(name);

    const [theme, setTheme] = useState(
      () => localStorage.getItem('co-theme') || 'light'
    );

    // True when running as installed PWA (standalone)
    const [isPWA] = useState(() =>
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches
    );

    useEffect(() => {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('co-theme', theme);
    }, [theme]);

    const toggleTheme = () =>
      setTheme(t => t === 'light' ? 'dark' : 'light');

    // Public tracking page — no auth required
    const _path = window.location.pathname;
    if (_path.startsWith('/track/')) {
        const _token = _path.split('/track/')[1];
        return <TrackingPage token={_token} />;
    }

    if (supaBaseError) {
        return <div className="min-h-screen flex justify-center items-center font-bold">{supaBaseErrorMessage}</div>;
    }

    // Client portal — completely separate from admin/warehouse app
    if (clientUser) {
        return (
            <ClientDashboard
                customer={clientUser}
                onLogout={() => {
                    sessionStorage.removeItem('verified_customer');
                    setClientUser(null);
                }}
            />
        );
    }

    if (!tenantReady) return <div className="min-h-screen flex justify-center items-center font-bold">Cargando...</div>;
    if (!user) return <LoginPortal onLogin={handleLogin} />;

    if (loading) {
        return <div className="min-h-screen flex justify-center items-center font-bold">Loading Data...</div>;
    }

    return (
        <div className="min-h-screen">
            <style>{`.hide-on-mobile { display: inline; } @media (max-width: 640px) { .hide-on-mobile { display: none; } } .nav-tabs::-webkit-scrollbar { display: none; }`}</style>
            <nav className="top-nav">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                        onClick={() => {
                            if (isAdminOrManager && window.innerWidth <= 768) {
                                setLogoMenuOpen(v => !v);
                            } else {
                                setCurrentView('customer_service');
                            }
                        }}
                        style={{
                            border: 'none',
                            borderRadius: '8px',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer',
                            flexShrink: 0,
                            padding: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transform: logoMenuOpen ? 'rotate(-8deg) scale(1.05)' : 'none',
                            transition: 'transform 180ms ease-out',
                        }}
                        title="SivarSend Home"
                    >
                        <img
                            src={SIVARSEND_LOGO}
                            alt="SivarSend"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', display: 'block' }}
                        />
                    </button>
                    <span className="logo hide-on-mobile">
                        <span style={{ fontWeight: '700' }}>{tenant?.company_name ?? 'SivarSend'}</span>
                    </span>
                </div>

                {isAdminOrManager && (
                    <div
                        ref={logoMenuRef}
                        className="logo-menu"
                        style={{
                            position: 'absolute',
                            top: '56px',
                            left: '16px',
                            width: '50vw',
                            maxWidth: '320px',
                            minWidth: '240px',
                            zIndex: 1000,
                            background: '#ffffff',
                            border: '1px solid #eaeaea',
                            borderRadius: '12px',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                            padding: '8px',
                            maxHeight: logoMenuOpen ? '500px' : '0',
                            opacity: logoMenuOpen ? 1 : 0,
                            pointerEvents: logoMenuOpen ? 'auto' : 'none',
                            overflow: logoMenuOpen ? 'visible' : 'hidden',
                            transition: 'max-height 220ms cubic-bezier(0.16, 1, 0.3, 1), opacity 180ms ease-out',
                        }}
                    >
                        <style>{`
                            .logo-menu-row:hover { background: #f5f5f5; }
                            .logo-menu-row:active { transform: scale(0.98); }
                            @media (min-width: 769px) { .logo-menu { display: none !important; } }
                        `}</style>
                        <div style={{
                            fontSize: '12px',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            color: '#666',
                            padding: '8px 10px 4px',
                        }}>
                            Navegación
                        </div>
                        {[
                            { key: 'admin_dashboard', label: 'Panel', icon: '⚙', show: userPermissions.canViewAdminDashboard },
                            { key: 'client_manager', label: 'Gestor de Clientes', icon: '👥', show: userPermissions.canViewClientes },
                            { key: 'customer_service', label: 'Nueva Entrada', icon: '📥', show: userPermissions.canViewEntrada },
                            { key: 'recepcion_digital', label: 'Recepción Digital', icon: '💬', show: userPermissions.canViewRecepcionDigital && hasFeature('recepcion_digital') },
                            { key: 'manifest', label: 'Manifiesto', icon: '📋', show: userPermissions.canViewManifest && !isViajero },
                            { key: 'guias', label: 'Guías', icon: '🚚', show: userPermissions.canViewGuias },
                            { key: 'facturacion', label: 'Facturación', icon: '💰', show: userPermissions.canViewFacturacion },
                            { key: 'finanzas', label: 'Finanzas', icon: '📊', show: userPermissions.canViewFinanzas },
                            { key: 'production', label: 'Producción', icon: '🏭', show: userPermissions.canExportCosteo && !isViajero },
                        ].filter(item => item.show).map(item => (
                            <button
                                key={item.key}
                                className="logo-menu-row"
                                onClick={() => setCurrentView(item.key)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    width: '100%',
                                    padding: '12px 10px',
                                    border: 'none',
                                    background: currentView === item.key ? 'rgba(216, 38, 44, 0.08)' : 'transparent',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '15px',
                                    fontWeight: currentView === item.key ? 600 : 500,
                                    color: currentView === item.key ? '#d8262c' : '#1a1a1a',
                                    textAlign: 'left',
                                    transition: 'background 120ms ease',
                                }}
                            >
                                <span>{item.icon}</span>
                                {item.label}
                            </button>
                        ))}
                    </div>
                )}

                <div
                    className="nav-tabs"
                    style={{
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        flexWrap: 'nowrap',
                        whiteSpace: 'nowrap',
                        scrollbarWidth: 'none',
                    }}
                >
                    {userPermissions.canViewAdminDashboard && (
                        <button
                            className={`nav-tab ${currentView === 'admin_dashboard' ? 'active' : ''}`}
                            onClick={() => setCurrentView('admin_dashboard')}
                            style={{ flexShrink: 0 }}
                        >
                            Panel
                        </button>
                    )}
                    {userPermissions.canViewClientes && (
                        <button
                            className={`nav-tab ${currentView === 'client_manager' ? 'active' : ''}`}
                            onClick={() => setCurrentView('client_manager')}
                            style={{ flexShrink: 0 }}
                        >
                            Gestor de Clientes
                        </button>
                    )}
                    {userPermissions.canViewEntrada && (
                        <button
                            className={`nav-tab ${currentView === 'customer_service' ? 'active' : ''}`}
                            onClick={() => setCurrentView('customer_service')}
                            style={{ flexShrink: 0 }}
                        >
                            Nueva Entrada
                        </button>
                    )}
                    {userPermissions.canViewRecepcionDigital && hasFeature('recepcion_digital') && (
                        <button
                            className={`nav-tab ${currentView === 'recepcion_digital' ? 'active' : ''}`}
                            onClick={() => setCurrentView('recepcion_digital')}
                            style={{ flexShrink: 0 }}
                        >
                            Recepción Digital
                        </button>
                    )}
                    {userPermissions.canViewManifest && !isViajero && (
                        <button
                            className={`nav-tab ${currentView === 'manifest' ? 'active' : ''}`}
                            onClick={() => setCurrentView('manifest')}
                            style={{ flexShrink: 0 }}
                        >
                            Manifiesto
                        </button>
                    )}
                    {userPermissions.canViewGuias && (
                        <button
                            className={`nav-tab ${currentView === 'guias' ? 'active' : ''}`}
                            onClick={() => setCurrentView('guias')}
                            style={{ flexShrink: 0 }}
                        >
                            Guías
                        </button>
                    )}
                    {userPermissions.canViewFacturacion && (
                        <button
                            className={`nav-tab ${currentView === 'facturacion' ? 'active' : ''}`}
                            onClick={() => setCurrentView('facturacion')}
                            style={{ flexShrink: 0 }}
                        >
                            Facturación
                        </button>
                    )}
                    {userPermissions.canViewFinanzas && (
                        <button
                            className={`nav-tab ${currentView === 'finanzas' ? 'active' : ''}`}
                            onClick={() => setCurrentView('finanzas')}
                            style={{ flexShrink: 0 }}
                        >
                            Finanzas
                        </button>
                    )}
                    {userPermissions.canExportCosteo && !isViajero && (
                        <button
                            className={`nav-tab ${currentView === 'production' ? 'active' : ''}`}
                            onClick={() => setCurrentView('production')}
                            style={{ flexShrink: 0 }}
                        >
                            Producción
                        </button>
                    )}
                </div>

                <div className="nav-right">
                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
                        {user?.role}
                    </span>
                    <div className="avatar">
                        {(user?.full_name || user?.username || 'AV')
                            .slice(0, 2).toUpperCase()}
                    </div>
                    <button className="theme-toggle" onClick={toggleTheme}>
                        {theme === 'light' ? '◐' : '○'}
                    </button>
                    <button onClick={async () => { await supabase.auth.signOut(); localStorage.removeItem('userRole'); setUser(null); }} className="btn-ghost" style={{ marginLeft: '8px' }}><LogOut size={16}/></button>
                </div>
            </nav>

            {/* ── MOBILE BOTTOM NAV — only visible on mobile via CSS ── */}
            <nav className="bottom-nav">
              {isAdminOrManager && (
                <button
                  className="bottom-nav-tab"
                  onClick={() => setLogoMenuOpen(v => !v)}
                >
                  <span style={{ fontSize: '18px', lineHeight: '20px', height: '20px' }}>☰</span>
                  Menú
                </button>
              )}
              {userPermissions.canViewAdminDashboard && !isAdminOrManager && (
                <button
                  className={`bottom-nav-tab ${currentView === 'admin_dashboard' ? 'active' : ''}`}
                  onClick={() => setCurrentView('admin_dashboard')}
                >
                  <span style={{ fontSize: '18px', lineHeight: '20px', height: '20px' }}>⚙</span>
                  Panel
                </button>
              )}
              {userPermissions.canViewClientes && (
                <button
                  className={`bottom-nav-tab ${currentView === 'client_manager' ? 'active' : ''}`}
                  onClick={() => setCurrentView('client_manager')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20" strokeWidth="1.8">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  Clientes
                </button>
              )}
              {userPermissions.canViewEntrada && !isAdminOrManager && (
                <button
                  className={`bottom-nav-tab ${currentView === 'customer_service' ? 'active' : ''}`}
                  onClick={() => setCurrentView('customer_service')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20" strokeWidth="1.8">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                  Entrada
                </button>
              )}
              {userPermissions.canViewManifest && !isViajero && (
                <button
                  className={`bottom-nav-tab ${currentView === 'manifest' ? 'active' : ''}`}
                  onClick={() => setCurrentView('manifest')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20" strokeWidth="1.8">
                    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                    <rect x="9" y="3" width="6" height="4" rx="1"/>
                    <path d="M9 12h6M9 16h4"/>
                  </svg>
                  Manifiesto
                </button>
              )}
              {userPermissions.canViewGuias && !isAdminOrManager && (
                <button
                  className={`bottom-nav-tab ${currentView === 'guias' ? 'active' : ''}`}
                  onClick={() => setCurrentView('guias')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20" strokeWidth="1.8">
                    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                  </svg>
                  Guías
                </button>
              )}
              {userPermissions.canViewFacturacion && (
                <button
                  className={`bottom-nav-tab ${currentView === 'facturacion' ? 'active' : ''}`}
                  onClick={() => setCurrentView('facturacion')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20" strokeWidth="1.8">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="8" y1="13" x2="16" y2="13"/>
                    <line x1="8" y1="17" x2="16" y2="17"/>
                  </svg>
                  Facturación
                </button>
              )}
              {userPermissions.canExportCosteo && !isViajero && (
                <button
                  className={`bottom-nav-tab ${currentView === 'production' ? 'active' : ''}`}
                  onClick={() => setCurrentView('production')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20" strokeWidth="1.8">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M9 9h6v6H9z"/>
                    <path d="M3 9h18M9 3v18"/>
                  </svg>
                  Producción
                </button>
              )}
            </nav>

            {/* ── PWA Hard Refresh — only visible in standalone
                installed PWA, never in browser ── */}
            {isPWA && (
              <button
                onClick={async () => {
                  try {
                    if ('serviceWorker' in navigator) {
                      const registrations =
                        await navigator.serviceWorker.getRegistrations();
                      for (const reg of registrations) {
                        await reg.unregister();
                      }
                    }
                    if ('caches' in window) {
                      const keys = await caches.keys();
                      await Promise.all(keys.map(k => caches.delete(k)));
                    }
                  } catch (e) {
                    console.warn('[HardRefresh] cleanup error:', e);
                  }
                  window.location.reload(true);
                }}
                title="Actualizar App"
                style={{
                  position: 'fixed',
                  bottom: '88px',
                  right: '16px',
                  zIndex: 999,
                  background: 'var(--avianca-red)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: '44px',
                  height: '44px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
                }}
              >
                🔄
              </button>
            )}

            <main className="page-container">
                {/* Viajero: Nueva Entrada monta el módulo de viaje directo — sin toggle casillero/encomienda.
                    Manifiesto y Producción viven como pestañas dentro del módulo. */}
                {currentView === 'customer_service' && userPermissions.canViewEntrada && isViajero && (
                    <ViajeroEncomienda />
                )}
                {currentView === 'customer_service' && userPermissions.canViewEntrada && !isViajero && (
                    <>
                        {/* Pending encomienda drafts — above the toggle */}
                        <PendingEncomiendas
                            lang={language}
                            onResume={(id) => {
                                setEntryMode('encomienda');
                                setResumeEncomiendaId(id);
                            }}
                        />

                        {/* CASILLERO / ENCOMIENDA toggle */}
                        <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-xl max-w-3xl mx-auto" style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                            <button
                                type="button"
                                onClick={() => setEntryMode('casillero')}
                                className={`flex-1 py-3 px-8 rounded-lg font-bold transition ${entryMode === 'casillero' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}
                                style={entryMode === 'casillero' ? { background: 'var(--tile)', color: 'var(--blue-text)', boxShadow: 'var(--tile-shadow)' } : { color: 'var(--text-tertiary)', background: 'transparent' }}
                            >
                                {t.casillero}
                            </button>
                            <button
                                type="button"
                                onClick={() => setEntryMode('encomienda')}
                                className={`flex-1 py-3 px-8 rounded-lg font-bold transition ${entryMode === 'encomienda' ? 'bg-orange-500 shadow text-white' : 'text-gray-500'}`}
                                style={entryMode === 'encomienda' ? { background: 'var(--avianca-red)', color: '#fff', boxShadow: '0 2px 8px var(--red-glow)' } : { color: 'var(--text-tertiary)', background: 'transparent' }}
                            >
                                {t.encomienda}
                            </button>
                        </div>

                        {/* Active session manifest banner */}
                        {activeSessionManifest && (
                            <div style={{
                                background: '#FAECE7',
                                border: '1px solid var(--avianca-red)',
                                borderRadius: '10px',
                                padding: '10px 14px',
                                marginBottom: '12px',
                                fontSize: '12px',
                                color: 'var(--avianca-red)',
                                fontWeight: '500',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}>
                                <span>
                                    Trabajando en manifiesto cerrado: {activeSessionManifest.id}
                                    — Los nuevos paquetes se agregarán a este manifiesto
                                </span>
                                <button
                                    onClick={() => setActiveSessionManifest(null)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--avianca-red)',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                >
                                    × Cerrar sesión
                                </button>
                            </div>
                        )}

                        {/* Conditional form */}
                        {entryMode === 'casillero' ? (
                            <WarehouseTerminal onSubmit={handleAddShipment} existingCustomers={customers} t={t} activeManifest={workingManifest} user={user} />
                        ) : (
                            <EncomiendaWizard
                                currentManifest={currentManifest}
                                user={user}
                                resumeId={resumeEncomiendaId}
                                onResumeConsumed={() => setResumeEncomiendaId(null)}
                            />
                        )}
                    </>
                )}
                {currentView === 'manifest' && userPermissions.canViewManifest && !isViajero && <LiveManifest shipments={shipments} boxes={boxes} onUpdate={handleUpdateShipment} onDeleteShipment={handleDeleteShipment} onAddBox={handleAddBox} onUpdateBox={handleUpdateBox} onDeleteBox={handleDeleteBox} t={t} currentManifest={workingManifest} liveManifest={currentManifest} onCloseManifest={handleCloseManifest} user={user} userPermissions={userPermissions} onSessionManifestChange={(manifest) => setActiveSessionManifest(manifest)} activeSessionManifest={activeSessionManifest} onReopenLastManifest={handleReopenLastManifest} />}
                {currentView === 'recepcion_digital' && userPermissions.canViewRecepcionDigital && hasFeature('recepcion_digital') && <RecepcionDigital user={user} />}
                {currentView === 'production' && userPermissions.canExportCosteo && !isViajero && <ProductionTab user={user} userPermissions={userPermissions} />}
                {currentView === 'guias' && userPermissions.canViewGuias && <GuiasPortal user={user} />}
                {currentView === 'facturacion' && userPermissions.canViewFacturacion && <FacturacionPanel user={user} />}
                {currentView === 'finanzas' && userPermissions.canViewFinanzas && <FinanzasPage userPermissions={userPermissions} />}
                {currentView === 'admin_dashboard' && userPermissions.canViewAdminDashboard && <AdminDashboard tenant={tenant} />}
                {currentView === 'client' && <ClientDashboard user={user} shipments={shipments} boxes={boxes} onUpdateShipment={handleUpdateShipment} t={t} />}
                {currentView === 'client_manager' && userPermissions.canViewClientes && <ClientManager userPermissions={userPermissions} user={user} />}
            </main>
        </div>
    );
};
export default App;